// src/api/axiosInstance.js
import axios from "axios";

const axiosApi = axios.create({
  baseURL: process.env.REACT_APP_ZULAL_API_URL || "",
  timeout: 15000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json, text/plain, */*",
  },
});

// Request interceptor (cookie auth usually needs nothing special)
axiosApi.interceptors.request.use(
  (config) => {
    // Optional: ensure withCredentials stays enabled per-request
    if (config.withCredentials === undefined) {
      config.withCredentials = true;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error && error.response) {
      const status = error.response.status;

      // Parse Retry-After header if present (seconds or HTTP-date)
      const retryHeader =
        error.response.headers &&
        (error.response.headers["retry-after"] ||
          error.response.headers["Retry-After"]);

      let retryAfter = null;
      if (retryHeader) {
        const asInt = parseInt(retryHeader, 10);
        if (!Number.isNaN(asInt)) {
          retryAfter = Math.max(0, asInt);
        } else {
          const parsed = Date.parse(retryHeader);
          if (!Number.isNaN(parsed)) {
            retryAfter = Math.max(0, Math.ceil((parsed - Date.now()) / 1000));
          }
        }
      }

      // Attach useful info for callers
      error.status = status;
      error.retryAfter = retryAfter;
      error.apiMessage =
        (error.response.data &&
          (error.response.data.message || error.response.data.error)) ||
        null;

      // ✅ 401 global handling (guest-safe)
      if (status === 401) {
        const isLogoutCall = error.config?.url?.includes("/logout");
        const skipUnauthorizedEvent =
          error.config?.skipUnauthorizedEvent === true || isLogoutCall;

        // Only emit global unauthorized when NOT skipped
        if (!skipUnauthorizedEvent) {
          try {
            window.dispatchEvent(
              new CustomEvent("api:unauthorized", {
                detail: {
                  status,
                  url: error.config?.url,
                  method: error.config?.method,
                },
              })
            );
          } catch (e) {
            // ignore
          }
        }
      }

      // ✅ 429 rate limiting (optional)
      if (status === 429) {
        try {
          window.dispatchEvent(
            new CustomEvent("api:rate-limited", { detail: { retryAfter } })
          );
        } catch (e) {
          // ignore
        }
      }
    } else if (error && error.request && !error.response) {
      // Network error / no response
      error.apiMessage =
        "No response from server. Check network or server status.";
    }

    return Promise.reject(error);
  }
);

export default axiosApi;
